import { useState, useEffect } from "react";
import { IoMdCheckmark } from "react-icons/io";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../styles/__labDialogAddEditTest.module.scss";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import {
  createLabTechnician,
  getPermittedTechnicianGenders,
  updateLabTechnician,
} from "../../services/labTechniciansService";
import { toast } from "../toast";
import {
  getMobileLengthIssue,
  limitMobileDigits,
  mobileLengthErrorText,
} from "../../utils/phoneValidation";

// Canonical "Male" / "Female" for stored values in any casing; anything else is
// returned as-is so a legacy value (e.g. "Other") can still be shown in edit mode.
const normalizeGender = (value) => {
  const v = String(value || "").trim();
  if (v.toLowerCase() === "male") return "Male";
  if (v.toLowerCase() === "female") return "Female";
  return v;
};

// The country code is picked separately, so the local number must not start with
// the trunk "0": leading zeros (and any spaces before them) are dropped while typing
// or pasting, and when an existing number is loaded for editing.
const stripLeadingZeros = (value) => String(value || "").replace(/^[\s0]+/, "");

export default function LabDialogAddEditTechnical({
  open,
  onClose,
  children,
  selectedTechnician,
  type = "add",
  onSuccess,
}) {
  const [name, setName] = useState("");
  const [gender, setGender] = useState("Male");
  const [status, setStatus] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("+964");
  const [mobileNumber, setMobileNumber] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  // Set by the first Save; from then on a too-short mobile number is flagged as it changes.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  // Genders the admin assigned to this lab ("Gender Preference"); null while loading.
  const [permittedGenders, setPermittedGenders] = useState(null);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // Technician's stored gender when editing. It stays selectable-as-is even if the
  // admin has since narrowed the lab's preference, so editing other details never
  // silently changes it; it just can't be picked again once changed.
  const originalGender =
    type === "edit" && selectedTechnician
      ? normalizeGender(selectedTechnician.gender)
      : "";

  const genderLabel = (value) => {
    if (value === "Male") return t("lab.technicianDialog.form.genderMale");
    if (value === "Female") return t("lab.technicianDialog.form.genderFemale");
    if (value === "Other") return t("lab.technicianDialog.form.genderOther");
    return value;
  };

  // Re-read the lab's permitted genders every time the dialog opens so a change made
  // in the Admin Panel is reflected without logging out.
  useEffect(() => {
    if (!open) {
      setPermittedGenders(null);
      return undefined;
    }
    let cancelled = false;
    getPermittedTechnicianGenders().then((genders) => {
      if (!cancelled) setPermittedGenders(genders);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Keep the selection on a permitted gender: default a new technician (or one with
  // no stored gender) to the first permitted option.
  useEffect(() => {
    if (!open || !permittedGenders) return;
    const keepsOriginal = !!originalGender && gender === originalGender;
    if (!permittedGenders.includes(gender) && !keepsOriginal) {
      setGender(permittedGenders[0]);
    }
  }, [open, permittedGenders, gender, originalGender]);

  // Country list mirrors the one used in the doctor app's patient
  // registration screen so flags + codes render the same way.
  const countries = [
    { code: "+964", iso: "iq", name: "Iraq" },
    { code: "+91", iso: "in", name: "India" },
    { code: "+966", iso: "sa", name: "Saudi Arabia" },
    { code: "+971", iso: "ae", name: "UAE" },
    { code: "+965", iso: "kw", name: "Kuwait" },
    { code: "+974", iso: "qa", name: "Qatar" },
    { code: "+973", iso: "bh", name: "Bahrain" },
    { code: "+968", iso: "om", name: "Oman" },
    { code: "+962", iso: "jo", name: "Jordan" },
    { code: "+961", iso: "lb", name: "Lebanon" },
    { code: "+20", iso: "eg", name: "Egypt" },
    { code: "+90", iso: "tr", name: "Turkey" },
    { code: "+98", iso: "ir", name: "Iran" },
    { code: "+92", iso: "pk", name: "Pakistan" },
    { code: "+880", iso: "bd", name: "Bangladesh" },
    { code: "+44", iso: "gb", name: "United Kingdom" },
    { code: "+1", iso: "us", name: "United States" },
    { code: "+49", iso: "de", name: "Germany" },
    { code: "+33", iso: "fr", name: "France" },
    { code: "+61", iso: "au", name: "Australia" },
    { code: "+86", iso: "cn", name: "China" },
    { code: "+81", iso: "jp", name: "Japan" },
    { code: "+82", iso: "kr", name: "South Korea" },
    { code: "+60", iso: "my", name: "Malaysia" },
    { code: "+63", iso: "ph", name: "Philippines" },
    { code: "+234", iso: "ng", name: "Nigeria" },
    { code: "+27", iso: "za", name: "South Africa" },
    { code: "+254", iso: "ke", name: "Kenya" },
    { code: "+55", iso: "br", name: "Brazil" },
    { code: "+52", iso: "mx", name: "Mexico" },
  ];

  // The dropdown lists countries A–Z by name.
  const sortedCountries = [...countries].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const selectedCountryData =
    countries.find((country) => country.code === selectedCountry) ||
    countries[0];

  // Digit count required for the selected country (libphonenumber-js metadata). Recomputed on
  // every render, so it follows a change of country code immediately: too long at once (e.g.
  // after switching country), too short once Save has been pressed.
  const mobileLengthIssue = getMobileLengthIssue(
    mobileNumber,
    selectedCountryData.iso,
  );
  const mobileLengthError =
    mobileLengthIssue && (submitAttempted || mobileLengthIssue.tooLong)
      ? mobileLengthErrorText(t, i18n.language, selectedCountryData, mobileLengthIssue)
      : "";

  // Close country dropdown when clicking outside the trigger / panel.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isCountryDropdownOpen &&
        !e.target.closest("[data-country-dropdown]")
      ) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCountryDropdownOpen]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");

      // If editing, populate form with selected technician data
      if (type === "edit" && selectedTechnician) {
        setName(
          selectedTechnician.name ||
            `${selectedTechnician.fullName || ""} ${selectedTechnician.surname || ""}`.trim(),
        );
        setGender(normalizeGender(selectedTechnician.gender));
        setStatus(
          selectedTechnician.status === "ACTIVE" ||
            selectedTechnician.status === "Active",
        );

        // Extract country code and mobile number
        if (selectedTechnician.mobileNumber) {
          const phoneNumber = selectedTechnician.mobileNumber.trim();

          if (phoneNumber.startsWith("+")) {
            // Try longest country code first so e.g. "+964" wins over "+1".
            const sortedCountries = [...countries].sort(
              (a, b) => b.code.length - a.code.length,
            );

            let matched = false;
            for (const country of sortedCountries) {
              if (phoneNumber.startsWith(country.code)) {
                setSelectedCountry(country.code);
                const numberPart = phoneNumber
                  .substring(country.code.length)
                  .replace(/[\s-]/g, "");
                setMobileNumber(stripLeadingZeros(numberPart));
                matched = true;
                break;
              }
            }

            // Legacy data fix: numbers saved with the old "+96" typo
            // (Iraq country code is "+964"). Treat "+96<digits>" as Iraq + the
            // digits that come after "+96", and remove any leading "4" that
            // may already be part of the original local number.
            if (!matched && phoneNumber.startsWith("+96")) {
              setSelectedCountry("+964");
              setMobileNumber(
                stripLeadingZeros(phoneNumber.substring(3).replace(/[\s-]/g, "")),
              );
              matched = true;
            }

            // Generic fallback: keep raw number, default to first country.
            if (!matched) {
              setSelectedCountry(countries[0].code);
              setMobileNumber(phoneNumber.replace(/[\s-]/g, ""));
            }
          } else {
            // No country code, just set the number
            setMobileNumber(stripLeadingZeros(phoneNumber));
          }
        } else {
          setMobileNumber("");
        }
      }
    } else {
      document.body.classList.remove("modal-open");
      // Reset form when closing
      resetForm();
    }

    return () => {
      document.body.classList.remove("modal-open"); // cleanup
    };
  }, [open, type, selectedTechnician]);

  const resetForm = () => {
    setName("");
    setGender("Male");
    setMobileNumber("");
    setStatus(true);
    setSelectedCountry("+964");
    setSubmitAttempted(false);
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    // Validation
    if (!name.trim()) {
      toast.error(t("lab.technicianDialog.validation.nameRequired"));
      return;
    }
    if (!mobileNumber.trim()) {
      toast.error(t("lab.technicianDialog.validation.mobileRequired"));
      return;
    }
    // Wrong digit count for the selected country: the message is shown under the field.
    if (mobileLengthIssue) return;
    // Permitted genders still loading (Save is disabled meanwhile).
    if (!permittedGenders) return;
    if (
      !permittedGenders.includes(gender) &&
      !(originalGender && gender === originalGender)
    ) {
      toast.error(t("lab.technicianDialog.validation.genderNotPermitted"));
      return;
    }

    setLoading(true);

    try {
      // Prepare phone number with country code
      const fullPhoneNumber = `${selectedCountry}${mobileNumber.replace(/\s/g, "")}`;

      // Split name into first and last name
      const nameParts = name.trim().split(" ");
      const fullName = nameParts[0] || "";
      const surname = nameParts.slice(1).join(" ") || "";

      const technicianData = {
        fullName,
        surname,
        name: name.trim(),
        gender,
        mobileNumber: fullPhoneNumber,
        status: status ? "ACTIVE" : "INACTIVE",
      };

      let response;
      if (type === "add") {
        response = await createLabTechnician(technicianData);
        toast.success(t("lab.technicianDialog.success.added"));
      } else {
        response = await updateLabTechnician(
          selectedTechnician.id,
          technicianData,
        );
        toast.success(t("lab.technicianDialog.success.updated"));
      }

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(response);
      }

      // Close dialog and reset form
      onClose();
      resetForm();
    } catch (error) {
      console.error("Error saving technician:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t("lab.technicianDialog.error.saveFailed");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`${styles["labDialog_wrapper"]}`}>
      <div className={`${styles["dialog-overlay"]}`}>
        <div className={styles["dialog-box"]}>
          {/* Close Button (X) */}
          <button className={styles["dialog-close"]} onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z"
                fill="currentcolor"
              />
            </svg>
          </button>

          {/* Title */}
          <div className={styles["mb-24"]}>
            <h2 className={styles["dialog-title"]}>
              {type === "add"
                ? t("lab.technicianDialog.addTitle")
                : t("lab.technicianDialog.editTitle")}
            </h2>
          </div>

          <div className={styles["form-design"]}>
            {/* Name */}
            <div className={styles["form-group"]}>
              <label>
                {t("lab.technicianDialog.form.name")}
                <span style={{ color: "red" }}> *</span>
              </label>
              <input
                type="text"
                placeholder={t("lab.technicianDialog.form.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={styles["text-input"]}
              />
            </div>

            {/* Gender */}
            <div className={styles["form-group"]}>
              <label>
                {t("lab.technicianDialog.form.gender")}
                <span style={{ color: "red" }}> *</span>
              </label>
              {/* Only the genders the admin assigned to this lab are offered. */}
              <select
                className={styles["select-input"]}
                value={permittedGenders ? gender : ""}
                onChange={(e) => setGender(e.target.value)}
                disabled={!permittedGenders}
                aria-busy={!permittedGenders}
              >
                {!permittedGenders && (
                  <option value="">
                    {t("lab.technicianDialog.form.genderLoading")}
                  </option>
                )}
                {permittedGenders &&
                  originalGender &&
                  !permittedGenders.includes(originalGender) && (
                    <option value={originalGender} disabled>
                      {genderLabel(originalGender)} (
                      {t("lab.technicianDialog.form.genderNotPermitted")})
                    </option>
                  )}
                {(permittedGenders || []).map((g) => (
                  <option key={g} value={g}>
                    {genderLabel(g)}
                  </option>
                ))}
              </select>
            </div>

            {/* Mobile Number */}
            <div className={styles["form-group"]}>
              <label htmlFor="mobile">
                {t("lab.technicianDialog.form.mobileNumber")}
                <span style={{ color: "red" }}> *</span>
              </label>
              <div style={{ position: "relative" }} data-country-dropdown>
                <div className={styles["mobile-input-container"]}>
                  <div
                    className={styles["country-selector"]}
                    onClick={() =>
                      setIsCountryDropdownOpen(!isCountryDropdownOpen)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <span className={styles["selected-flag"]}>
                      <img
                        src={`https://flagcdn.com/24x18/${selectedCountryData?.iso}.png`}
                        alt={selectedCountryData?.name}
                        style={{
                          width: "24px",
                          height: "18px",
                          borderRadius: "2px",
                          objectFit: "cover",
                        }}
                      />
                      <span className={styles["selected-countryCode"]}>
                        {selectedCountryData.code}
                      </span>
                    </span>
                    <ChevronDown
                      size={14}
                      className={styles["dropdown-arrow"]}
                    />
                  </div>

                  <input
                    type="tel"
                    id="mobile"
                    placeholder={t(
                      "lab.technicianDialog.form.mobilePlaceholder",
                    )}
                    value={mobileNumber}
                    onChange={(e) =>
                      // Digits only, no leading 0, and no more digits than the
                      // selected country's mobile numbers have.
                      setMobileNumber(
                        limitMobileDigits(e.target.value, selectedCountryData.iso),
                      )
                    }
                    aria-invalid={!!mobileLengthError || undefined}
                    aria-describedby={
                      mobileLengthError ? "mobile-error" : undefined
                    }
                    className={styles["phone-input"]}
                  />
                </div>

                {/* Custom dropdown panel - rendered OUTSIDE the input container
                    so it isn't clipped by `overflow: hidden`. */}
                {isCountryDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: "240px",
                      maxHeight: "220px",
                      overflowY: "auto",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1100,
                      marginTop: "4px",
                    }}
                  >
                    {sortedCountries.map((country) => (
                      <div
                        key={country.code}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCountry(country.code);
                          setIsCountryDropdownOpen(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontSize: "13px",
                          backgroundColor:
                            selectedCountry === country.code
                              ? "#e6f7f7"
                              : "#fff",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f3f4f6")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            selectedCountry === country.code
                              ? "#e6f7f7"
                              : "#fff")
                        }
                      >
                        <img
                          src={`https://flagcdn.com/24x18/${country.iso}.png`}
                          alt={country.name}
                          style={{
                            width: "24px",
                            height: "18px",
                            borderRadius: "2px",
                            objectFit: "cover",
                          }}
                        />
                        <span style={{ fontWeight: "500" }}>
                          {country.code}
                        </span>
                        <span style={{ color: "#6b7280" }}>{country.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {mobileLengthError && (
                <p id="mobile-error" role="alert" className={styles["rule-invalid"]}>
                  {mobileLengthError}
                </p>
              )}
            </div>

            {/* Status field is hidden per product decision — technicians default
                to ACTIVE and the availability status is not shown/edited here.
                The `status` state is still submitted (see handleSubmit) so new
                technicians are created ACTIVE and edits preserve the existing
                status. */}
          </div>

          {/* Footer */}
          <div className={`${styles["dialog-footer"]}`}>
            <button
              className={`${styles["btn-outlined-1"]}`}
              onClick={onClose}
              disabled={loading}
            >
              {t("lab.technicianDialog.buttons.cancel")}
            </button>
            <button
              className={styles["btn-1"]}
              onClick={handleSave}
              disabled={loading || !permittedGenders}
            >
              <IoMdCheckmark />{" "}
              {loading
                ? t("lab.technicianDialog.buttons.saving")
                : t("lab.technicianDialog.buttons.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
